import {MESSARIO_LAUNCH_PENDING_MAX_AGE} from '@lib/messarioLaunch/pending';

/**
 * Tab-scoped copy of the one-shot launch token from `#messario_launch=`.
 *
 * The fragment is stripped from the URL (`replaceState`) as soon as the page loads, but the panel
 * resolve that turns it into `messario_launch_pending` is asynchronous. A reload in between
 * (service worker unregister/reload, `swfix` navigation) would lose the token and open the default
 * account without a dialog. Keeping the token in sessionStorage until the resolve settles lets the
 * next boot retry it. Shares the pending TTL.
 */
export const MESSARIO_LAUNCH_TOKEN_KEY = 'messario_launch_token';
export const MESSARIO_LAUNCH_TOKEN_MAX_AGE = MESSARIO_LAUNCH_PENDING_MAX_AGE;

export type MessarioLaunchStoredToken = {
  token: string,
  createdAt: number
};

export function saveMessarioLaunchToken(
  token: string,
  storage: Storage = window.sessionStorage,
  now = Date.now()
) {
  const stored: MessarioLaunchStoredToken = {token, createdAt: now};
  storage.setItem(MESSARIO_LAUNCH_TOKEN_KEY, JSON.stringify(stored));
}

export function clearMessarioLaunchToken(
  storage: Storage = window.sessionStorage
) {
  storage.removeItem(MESSARIO_LAUNCH_TOKEN_KEY);
}

export function getMessarioLaunchToken(
  storage: Storage = window.sessionStorage,
  now = Date.now()
): {token?: string, stale: boolean} {
  const value = storage.getItem(MESSARIO_LAUNCH_TOKEN_KEY);
  if(!value) {
    return {stale: false};
  }

  try {
    const stored = JSON.parse(value) as MessarioLaunchStoredToken;
    if(
      typeof stored.token !== 'string' ||
      !stored.token ||
      typeof stored.createdAt !== 'number'
    ) {
      clearMessarioLaunchToken(storage);
      return {stale: true};
    }

    if(now - stored.createdAt > MESSARIO_LAUNCH_TOKEN_MAX_AGE) {
      clearMessarioLaunchToken(storage);
      return {stale: true};
    }

    return {token: stored.token, stale: false};
  } catch(err) {
    clearMessarioLaunchToken(storage);
    return {stale: true};
  }
}
